import { useEffect, useState } from 'react';
import { Bot, CheckCircle2, Plus, Pencil, Trash2, Power, Eye, EyeOff, Zap } from 'lucide-react';
import { toast } from 'sonner';
import {
  listModels, createModel, deleteModel,
  listTemplates, createTemplate, updateTemplate, deleteTemplate,
  activateTemplate, testKey,
  type AIModel, type AITemplate,
} from '../services/admin_api';
import { extractErrorMessage } from '@/services/api';

type Tab = 'templates' | 'anthropic' | 'openai' | 'gemini';

export function AIConfiguration() {
  const [tab, setTab] = useState<Tab>('templates');
  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Bot className="h-6 w-6 text-primary" />
          AI Configuration
        </h1>
        <p className="text-sm text-text-secondary">
          Manage models, create templates, and control which AI runs for all users.
        </p>
      </div>

      <div className="flex gap-1 rounded-lg bg-white p-1 shadow-card w-fit">
        <TabButton active={tab === 'templates'} onClick={() => setTab('templates')}>Templates</TabButton>
        <TabButton active={tab === 'anthropic'} onClick={() => setTab('anthropic')}>Anthropic</TabButton>
        <TabButton active={tab === 'openai'} onClick={() => setTab('openai')}>OpenAI</TabButton>
        <TabButton active={tab === 'gemini'} onClick={() => setTab('gemini')}>Gemini</TabButton>
      </div>

      {tab === 'templates' && <TemplatesTab />}
      {tab !== 'templates' && <ModelsTab provider={tab} />}
    </div>
  );
}

function TabButton({
  active, onClick, children,
}: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick}
      className={[
        'rounded-md px-3 py-1.5 text-sm transition',
        active ? 'bg-primary text-white' : 'text-text-primary hover:bg-black/5',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

// --- Templates tab -------------------------------------------------------

function TemplatesTab() {
  const [items, setItems] = useState<AITemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<AITemplate | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  async function refresh() {
    setLoading(true);
    try { setItems(await listTemplates()); }
    catch (e) { toast.error(extractErrorMessage(e, 'Load failed')); }
    finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, []);

  const onActivate = async (t: AITemplate) => {
    try {
      await activateTemplate(t.id);
      toast.success(`Activated: ${t.name}`);
      refresh();
    } catch (e) { toast.error(extractErrorMessage(e, 'Activate failed')); }
  };
  const onDelete = async (t: AITemplate) => {
    if (!confirm(`Delete template "${t.name}"?`)) return;
    try {
      await deleteTemplate(t.id);
      toast.success('Deleted');
      refresh();
    } catch (e) { toast.error(extractErrorMessage(e, 'Delete failed')); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">AI Templates</h2>
          <p className="text-xs text-text-secondary">
            The active template controls ALL chatbots. Only one can be active.
          </p>
        </div>
        <button className="btn-primary" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1 h-4 w-4" /> New Template
        </button>
      </div>

      {loading ? (
        <div className="card p-6 text-center text-sm text-text-secondary">Loading…</div>
      ) : items.length === 0 ? (
        <div className="card p-8 text-center text-sm text-text-secondary">
          No templates yet. Create one and activate it so users can chat.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((t) => (
            <div key={t.id}
              className={[
                'card flex items-center gap-3 p-4 transition',
                t.is_active ? 'ring-2 ring-primary/40 bg-primary/5' : '',
              ].join(' ')}
            >
              <CheckCircle2 className={['h-5 w-5', t.is_active ? 'text-green-600' : 'text-text-secondary/30'].join(' ')} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{t.name}</span>
                  {t.is_active && (
                    <span className="rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-green-700">
                      Active
                    </span>
                  )}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-text-secondary">
                  <span className="rounded-md bg-black/5 px-2 py-0.5 font-medium capitalize">{t.provider}</span>
                  <span className="font-mono">{t.exact_model_id}</span>
                  <span>·</span>
                  <span>{t.daily_token_limit === 0 ? 'unlimited' : `${t.daily_token_limit.toLocaleString()} tokens / user / day`}</span>
                  {t.description && <><span>·</span><span className="italic">{t.description}</span></>}
                </div>
              </div>
              <div className="flex items-center gap-1">
                {!t.is_active && (
                  <button className="btn-ghost text-xs" onClick={() => onActivate(t)}>
                    <Power className="mr-1 h-3.5 w-3.5" />Activate
                  </button>
                )}
                <button className="btn-ghost text-xs" onClick={() => setEditing(t)}>
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                {!t.is_active && (
                  <button className="btn-ghost text-xs text-red-600 hover:bg-red-50" onClick={() => onDelete(t)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {(createOpen || editing) && (
        <TemplateModal
          existing={editing}
          onClose={() => { setCreateOpen(false); setEditing(null); }}
          onSaved={() => { setCreateOpen(false); setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}

function TemplateModal({
  existing, onClose, onSaved,
}: { existing: AITemplate | null; onClose: () => void; onSaved: () => void }) {
  const isEdit = !!existing;
  const [name, setName] = useState(existing?.name ?? '');
  const [provider, setProvider] = useState<'anthropic' | 'openai' | 'gemini'>(existing?.provider ?? 'anthropic');
  const [model, setModel] = useState(existing?.exact_model_id ?? '');
  const [agenticModel, setAgenticModel] = useState(existing?.agentic_model_id ?? '');
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [limit, setLimit] = useState<number>(existing?.daily_token_limit ?? 10000);
  const [description, setDescription] = useState(existing?.description ?? '');
  const [models, setModels] = useState<AIModel[]>([]);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    listModels(provider).then(setModels).catch(() => setModels([]));
  }, [provider]);

  // Split the catalog into Reasoning vs Agentic so each dropdown only
  // shows its appropriate options. Detection is name-based and matches
  // the backend's isReasoningModel logic (gpt-5*, o1*, o3*, o4*).
  const reasoningModels = models.filter((m) => {
    const id = m.exact_model_id.toLowerCase();
    return id.startsWith('gpt-5') || id.startsWith('o1') || id.startsWith('o3') || id.startsWith('o4');
  });
  const agenticModels = models.filter((m) => !reasoningModels.includes(m));

  const onTest = async () => {
    if (!apiKey || !model) {
      toast.error('Provider, model, and API key all required to test.');
      return;
    }
    setTesting(true);
    try {
      await testKey({ provider, exact_model_id: model, api_key: apiKey });
      toast.success('Key works.');
    } catch (e) { toast.error(extractErrorMessage(e, 'Key test failed')); }
    finally { setTesting(false); }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isEdit) {
        await updateTemplate(existing!.id, {
          name: name.trim(), provider, exact_model_id: model,
          agentic_model_id: agenticModel || undefined,
          api_key: apiKey || undefined, daily_token_limit: Number(limit),
          description: description.trim() || undefined,
        });
        toast.success('Saved');
      } else {
        await createTemplate({
          name: name.trim(), provider, exact_model_id: model,
          agentic_model_id: agenticModel || undefined,
          api_key: apiKey,
          daily_token_limit: Number(limit), description: description.trim() || undefined,
        });
        toast.success('Created');
      }
      onSaved();
    } catch (err) { toast.error(extractErrorMessage(err, 'Save failed')); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <form onSubmit={onSubmit} className="w-full max-w-md rounded-lg bg-white p-5 shadow-elev" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-semibold">{isEdit ? 'Edit AI Template' : 'Create AI Template'}</h3>
        <p className="mb-3 text-xs text-text-secondary">Configure provider, model, and API key.</p>
        <div className="space-y-3">
          <Field label="Template Name">
            <input className="input" required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Premium AI" />
          </Field>
          <Field label="Provider">
            <select className="input" value={provider} onChange={(e) => { setProvider(e.target.value as typeof provider); setModel(''); setAgenticModel(''); }}>
              <option value="anthropic">Anthropic</option>
              <option value="openai">OpenAI</option>
              <option value="gemini">Gemini</option>
            </select>
          </Field>
          <Field label="Reasoning Model (main agent — for planning + tool calling)">
            <select className="input" value={model} onChange={(e) => setModel(e.target.value)} required>
              <option value="">Select reasoning model</option>
              <optgroup label="Reasoning (recommended)">
                {reasoningModels.map((m) => (
                  <option key={m.id} value={m.exact_model_id}>
                    {m.display_name} ({m.exact_model_id})
                  </option>
                ))}
              </optgroup>
              {agenticModels.length > 0 && (
                <optgroup label="Agentic (fallback if no reasoning model available)">
                  {agenticModels.map((m) => (
                    <option key={m.id} value={m.exact_model_id}>
                      {m.display_name} ({m.exact_model_id})
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            <div className="mt-1 text-[10px] text-text-secondary">
              Used by the main agent for every chat turn. Reasoning models give the best multi-step accuracy + Hinglish handling.
            </div>
          </Field>
          <Field label="Agentic Model (cheap classifier — auto-skipped if blank)">
            <select className="input" value={agenticModel} onChange={(e) => setAgenticModel(e.target.value)}>
              <option value="">(use backend default — gpt-4o-mini)</option>
              {agenticModels.map((m) => (
                <option key={m.id} value={m.exact_model_id}>
                  {m.display_name} ({m.exact_model_id})
                </option>
              ))}
            </select>
            <div className="mt-1 text-[10px] text-text-secondary">
              Used internally for the intent classifier (50-token JSON task). Pick a fast lightweight model to keep classification quick. Leave blank to reuse the main reasoning model.
            </div>
          </Field>
          <Field label={isEdit ? 'API Key (leave blank to keep)' : 'API Key'}>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input className="input pr-8"
                  type={showKey ? 'text' : 'password'}
                  value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                  placeholder={isEdit ? '••••••• (keep existing)' : 'sk-...'}
                  required={!isEdit}
                />
                <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-text-secondary"
                  onClick={() => setShowKey((v) => !v)}>
                  {showKey ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </button>
              </div>
              <button type="button" onClick={onTest} disabled={testing || !apiKey} className="btn-ghost text-xs whitespace-nowrap">
                <Zap className="mr-1 h-3 w-3" /> {testing ? 'Testing…' : 'Test'}
              </button>
            </div>
          </Field>
          <Field label="Token Limit / User / Day">
            <input type="number" min={0} className="input" value={limit} onChange={(e) => setLimit(Number(e.target.value))} />
            <div className="mt-1 text-[10px] text-text-secondary">0 = unlimited</div>
          </Field>
          <Field label="Description (optional)">
            <textarea className="input resize-none" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Notes…" />
          </Field>
        </div>
        <div className="mt-4 flex items-center justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={busy}>
            {busy ? 'Saving…' : (isEdit ? 'Save' : 'Create')}
          </button>
        </div>
      </form>
    </div>
  );
}

// --- Models tab (per-provider) -------------------------------------------

function ModelsTab({ provider }: { provider: 'anthropic' | 'openai' | 'gemini' }) {
  const [items, setItems] = useState<AIModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);

  async function refresh() {
    setLoading(true);
    try { setItems(await listModels(provider)); }
    catch (e) { toast.error(extractErrorMessage(e, 'Load failed')); }
    finally { setLoading(false); }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [provider]);

  const providerLabel = provider[0].toUpperCase() + provider.slice(1);

  const onDelete = async (m: AIModel) => {
    if (!confirm(`Delete model "${m.display_name}"?`)) return;
    try {
      await deleteModel(m.id);
      toast.success('Deleted');
      refresh();
    } catch (e) { toast.error(extractErrorMessage(e, 'Delete failed')); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">{providerLabel} Models</h2>
          <p className="text-xs text-text-secondary">These models appear in the template dropdown.</p>
        </div>
        <button className="btn-primary" onClick={() => setAddOpen(true)}>
          <Plus className="mr-1 h-4 w-4" /> Add Model
        </button>
      </div>

      {loading ? (
        <div className="card p-6 text-center text-sm text-text-secondary">Loading…</div>
      ) : items.length === 0 ? (
        <div className="card p-8 text-center text-sm text-text-secondary">No {providerLabel} models yet.</div>
      ) : (
        <div className="space-y-2">
          {items.map((m) => (
            <div key={m.id} className="card flex items-center gap-3 p-4">
              <div className="flex-1 min-w-0">
                <div className="font-medium">{m.display_name}</div>
                <div className="mt-0.5 text-xs text-text-secondary">
                  <span className="font-mono">{m.exact_model_id}</span>
                  {m.description && <><span> · </span><span className="italic">{m.description}</span></>}
                </div>
              </div>
              <button className="btn-ghost text-red-600 hover:bg-red-50" onClick={() => onDelete(m)}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {addOpen && (
        <AddModelModal provider={provider} onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); refresh(); }} />
      )}
    </div>
  );
}

function AddModelModal({
  provider, onClose, onSaved,
}: { provider: string; onClose: () => void; onSaved: () => void }) {
  const [displayName, setDisplayName] = useState('');
  const [exactID, setExactID] = useState('');
  const [description, setDescription] = useState('');
  const [contextWindow, setContextWindow] = useState<number>(128000);
  const [maxOutputTokens, setMaxOutputTokens] = useState<number>(4096);
  const [supportsTools, setSupportsTools] = useState<boolean>(true);
  const [supportsVision, setSupportsVision] = useState<boolean>(false);
  const [supportsThinking, setSupportsThinking] = useState<boolean>(false);
  const [supportsCaching, setSupportsCaching] = useState<boolean>(false);
  const [providerOptionsJSON, setProviderOptionsJSON] = useState<string>('{}');
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    let providerOptions: Record<string, unknown> | undefined;
    try {
      providerOptions = providerOptionsJSON.trim() ? JSON.parse(providerOptionsJSON) : undefined;
    } catch {
      toast.error('Provider options is not valid JSON');
      setBusy(false);
      return;
    }
    try {
      await createModel({
        provider,
        display_name: displayName.trim(),
        exact_model_id: exactID.trim(),
        description: description.trim() || undefined,
        context_window: contextWindow,
        max_output_tokens: maxOutputTokens,
        supports_tools: supportsTools,
        supports_vision: supportsVision,
        supports_thinking: supportsThinking,
        supports_caching: supportsCaching,
        provider_options: providerOptions,
      });
      toast.success('Added');
      onSaved();
    } catch (err) { toast.error(extractErrorMessage(err, 'Add failed')); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <form onSubmit={onSubmit} className="w-full max-w-lg rounded-lg bg-white p-5 shadow-elev max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-semibold">Add {provider[0].toUpperCase() + provider.slice(1)} Model</h3>
        <p className="mb-3 text-xs text-text-secondary">Enter the exact model ID used in API calls + capability flags.</p>
        <div className="space-y-3">
          <Field label="Display Name">
            <input className="input" required value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="e.g. Claude Sonnet 4.6" />
          </Field>
          <Field label="Exact Model ID (used in code)">
            <input className="input font-mono" required value={exactID} onChange={(e) => setExactID(e.target.value)} placeholder="e.g. claude-sonnet-4-6" />
          </Field>
          <Field label="Description (optional)">
            <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short description…" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Context Window (tokens)">
              <input className="input" type="number" min={1024} value={contextWindow}
                onChange={(e) => setContextWindow(parseInt(e.target.value, 10) || 0)} />
            </Field>
            <Field label="Max Output Tokens">
              <input className="input" type="number" min={128} value={maxOutputTokens}
                onChange={(e) => setMaxOutputTokens(parseInt(e.target.value, 10) || 0)} />
            </Field>
          </div>
          <div>
            <p className="mb-1 text-xs font-medium text-text-secondary">Capabilities</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <Checkbox label="Tools / function calling" checked={supportsTools} onChange={setSupportsTools} />
              <Checkbox label="Vision (images)" checked={supportsVision} onChange={setSupportsVision} />
              <Checkbox label="Extended thinking" checked={supportsThinking} onChange={setSupportsThinking} />
              <Checkbox label="Prompt caching" checked={supportsCaching} onChange={setSupportsCaching} />
            </div>
          </div>
          <button type="button" className="text-xs underline text-text-secondary"
            onClick={() => setShowAdvanced((v) => !v)}>
            {showAdvanced ? 'Hide' : 'Show'} provider quirks (advanced)
          </button>
          {showAdvanced && (
            <Field label="Provider Options (JSON)">
              <textarea className="input font-mono text-xs" rows={4}
                value={providerOptionsJSON}
                onChange={(e) => setProviderOptionsJSON(e.target.value)}
                placeholder='{"thinking_shape":"adaptive","supports_temperature":false}' />
              <p className="mt-1 text-xs text-text-secondary">
                Anthropic: <code>thinking_shape</code> (adaptive | enabled | none),
                <code> supports_temperature</code> (bool).
                OpenAI: <code>reasoning_token_floor</code> (int, e.g. 25000),
                <code> supports_temperature</code> (bool).
              </p>
            </Field>
          )}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={busy}>{busy ? 'Adding…' : 'Add Model'}</button>
        </div>
      </form>
    </div>
  );
}

function Checkbox({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium">{label}</span>
      {children}
    </label>
  );
}
